import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { toast } from "sonner";
import { Loader2, MessageSquare, ShieldX, FileText, Send, Quote } from "lucide-react";
import { api, apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const fmt = (d) => (d ? new Date(d).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "");

export default function SharedCvReview() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [author, setAuthor] = useState(() => localStorage.getItem("cv_reviewer_name") || "");
  const [text, setText] = useState("");
  const [anchor, setAnchor] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get(`/cv/shared/${token}`).then(({ data }) => setData(data)).catch(() => setData({ error: true })); }, [token]);

  const quoteSelection = () => { const s = (window.getSelection()?.toString() || "").trim(); if (!s) { toast.error("Sélectionnez d'abord un passage du document"); return; } setAnchor(s.slice(0, 200)); };
  const send = async () => {
    if (text.trim().length < 2) { toast.error("Écrivez un commentaire"); return; }
    setBusy(true);
    try {
      const { data: c } = await api.post(`/cv/shared/${token}/comments`, { author, text, anchor });
      localStorage.setItem("cv_reviewer_name", author);
      setData((d) => ({ ...d, comments: [...(d.comments || []), c] })); setText(""); setAnchor(""); toast.success("Commentaire envoyé");
    } catch (e) { toast.error(apiError(e, "Envoi impossible")); } finally { setBusy(false); }
  };

  if (!data) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="animate-spin text-[#0E7C7B]" size={28} /></div>;
  if (data.error) return (
    <div className="mx-auto flex min-h-[60vh] max-w-xl items-center px-4 py-16" data-testid="cv-shared-invalid">
      <div className="w-full rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100"><ShieldX size={34} className="text-red-600" /></div>
        <h1 className="mt-4 font-display text-2xl font-bold text-navy">Lien invalide ou révoqué</h1>
        <p className="mt-1 text-sm text-slate-500">Demandez à l'auteur un nouveau lien de partage.</p>
      </div>
    </div>
  );

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6" data-testid="cv-shared-page">
      <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-[#0E7C7B]"><FileText size={14} /> Cabinet CARIS Santé — Relecture d'un document</p>
      <h1 className="mt-2 font-display text-2xl font-bold text-navy sm:text-3xl" data-testid="cv-shared-title">{data.title}</h1>
      <p className="mt-1 text-sm text-slate-500">{data.doc_type_label} · {data.status === "finalise" ? "Finalisé" : "Brouillon"} · mis à jour le {fmt(data.updated_at)}</p>
      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_380px]">
        <div className="cv-editor rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-700 shadow-sm" data-testid="cv-shared-content" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.content || "") }} />
        <div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="cv-shared-comment-form">
            <p className="flex items-center gap-2 font-semibold text-navy"><MessageSquare size={16} className="text-[#0E7C7B]" /> Laisser un commentaire</p>
            <Input value={author} onChange={(e) => setAuthor(e.target.value)} placeholder="Votre nom (facultatif)" data-testid="cv-shared-author" className="mt-3 h-9 text-sm" />
            <div className="mt-2 flex items-center gap-2">
              <Button onClick={quoteSelection} variant="outline" size="sm" className="h-8 rounded-full border-slate-300 text-xs text-slate-600" data-testid="cv-shared-quote"><Quote size={12} className="mr-1" /> Citer le passage sélectionné</Button>
              {anchor && <button onClick={() => setAnchor("")} className="text-xs text-slate-400 hover:text-red-500">effacer</button>}
            </div>
            {anchor && <p className="mt-2 border-l-2 border-[#0E7C7B] pl-2 text-xs italic text-slate-500" data-testid="cv-shared-anchor">« {anchor} »</p>}
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder="Votre remarque, suggestion ou correction…" data-testid="cv-shared-text" className="mt-2 text-sm" />
            <Button onClick={send} disabled={busy} data-testid="cv-shared-send" className="mt-2 w-full rounded-full bg-[#0E7C7B] text-sm text-white hover:bg-[#0c6a69]">{busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Send size={14} className="mr-1" />} Envoyer</Button>
          </div>
          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-sm font-semibold text-navy">Commentaires ({(data.comments || []).length})</p>
            {(data.comments || []).length === 0 ? <p className="mt-2 text-xs text-slate-400">Soyez le premier à commenter.</p> : (
              <ul className="mt-2 space-y-2" data-testid="cv-shared-comments">
                {data.comments.map((c) => (
                  <li key={c.id} className="rounded-lg bg-slate-50 p-3 text-xs">
                    <p className="font-semibold text-navy">{c.author} <span className="font-normal text-slate-400">· {fmt(c.created_at)}</span></p>
                    {c.anchor && <p className="mt-1 border-l-2 border-[#0E7C7B] pl-2 italic text-slate-500">« {c.anchor} »</p>}
                    <p className="mt-1 whitespace-pre-wrap text-slate-700">{c.text}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
